/**
 * Faction delegates (owner's call 2026-11-02 15:36: "only leader, co leader, delegates … delegates should be selectable for
 * each. Leader/ co leader should basically get a tier panel of their own for their faction for each user"). One list per
 * faction: the leader or co-leader ticks, per member, what each may do. Leader and co-leader may always do everything;
 * nobody else unless ticked. Positions come from trusted data only (Torn's member list read with a key — never what a page
 * or a client says).
 *
 * Kept by the Omni-Torn server (apps/api/src/faction-delegates.ts → data/faction-delegates.json), read by the Discord bot
 * from the same file. Both check through `delegateMay` below. Editing the list itself is never delegable.
 */

export const DELEGATE_PERMS = ['discord', 'bot-settings', 'oc-rules', 'claims', 'bank', 'applications', 'watch', 'contracts'] as const;
export type DelegatePerm = (typeof DELEGATE_PERMS)[number];

export const DELEGATE_PERM_INFO: Record<DelegatePerm, { name: string; what: string }> = {
  discord: { name: 'Discord servers', what: 'Tie the faction to a Discord server (or remove one) and pick its home server.' },
  'bot-settings': { name: 'Discord bot settings', what: 'The faction’s alerts, tracked armory items and /war enemy in its Discord servers.' },
  'oc-rules': { name: 'OC rules', what: 'Publish the faction’s OC rules (or take them down).' },
  claims: { name: 'War claims', what: 'Set how long a hit claim lasts, and wipe every claim.' },
  bank: { name: 'Bank', what: 'See everyone’s balances, money moves and vault requests on the website.' },
  applications: { name: 'Applications', what: 'See applications to the faction (with each applicant’s estimate).' },
  watch: { name: 'Watch list', what: 'Add and remove factions on the faction’s watch list.' },
  contracts: { name: 'Contracts', what: 'Set up revive and merc contracts, accept and end them for the faction.' },
};

export interface DelegateChange {
  at: number;
  /** Who changed it (a leader or co-leader; null once they deleted their Omni-Torn data). */
  by: { id: number; name: string } | null;
  /** Whose permissions (0 once they deleted their Omni-Torn data). */
  player: number;
  perms: DelegatePerm[];
}

export interface FactionDelegatesRecord {
  /** Torn player id → what they may do. */
  members: Record<string, DelegatePerm[]>;
  /** Who changed what, newest last (the newest DELEGATE_LOG_MAX, plus older ones made by players on terms KEEP_ALL_TERMS). */
  log: DelegateChange[];
}

export interface DelegatesDoc {
  factions: Record<string, FactionDelegatesRecord>;
  /** Old lists already brought in (the OC-rules editors, the bot's named members). */
  imported: string[];
}

export const DELEGATES_MAX = 50;
/**
 * Read cap: lists brought in from older ones (up to 50 OC-rules editors + 25 named members) may hold more than
 * DELEGATES_MAX — nobody migrated is ever dropped; a save may not grow a list past DELEGATES_MAX (or its size, if larger).
 */
export const DELEGATES_READ_MAX = 200;
export const DELEGATE_LOG_MAX = 100;

export const isLeaderPosition = (position: string | null | undefined): boolean => !!position && /^(leader|co-leader)$/i.test(position.trim());

const isPerm = (x: unknown): x is DelegatePerm => typeof x === 'string' && (DELEGATE_PERMS as readonly string[]).includes(x);
export const cleanPerms = (raw: unknown): DelegatePerm[] => (Array.isArray(raw) ? DELEGATE_PERMS.filter((p) => raw.includes(p)) : []);

/** Only what's known, whole numbers, sizes capped. */
export function readDelegatesDoc(raw: unknown): DelegatesDoc {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: DelegatesDoc = { factions: {}, imported: Array.isArray(o.imported) ? o.imported.filter((x): x is string => typeof x === 'string').slice(0, 20) : [] };
  const fs = o.factions && typeof o.factions === 'object' && !Array.isArray(o.factions) ? (o.factions as Record<string, unknown>) : {};
  for (const [fid, v] of Object.entries(fs)) {
    if (!/^\d{1,9}$/.test(fid) || !v || typeof v !== 'object') continue;
    const r = v as { members?: unknown; log?: unknown };
    const members: Record<string, DelegatePerm[]> = {};
    for (const [pid, perms] of Object.entries(r.members && typeof r.members === 'object' ? (r.members as Record<string, unknown>) : {}).slice(0, DELEGATES_READ_MAX)) {
      const p = cleanPerms(perms);
      if (/^\d{1,9}$/.test(pid) && p.length) members[pid] = p;
    }
    // Every change kept is read (the store trims: terms 19 keeps changes made by players on it with no end date).
    const log: DelegateChange[] = (Array.isArray(r.log) ? r.log : [])
      .map((x) => x as Record<string, unknown>)
      .filter((x) => x && Number.isFinite(x.at) && Number.isInteger(x.player))
      .map((x) => {
        const by = x.by as { id?: unknown; name?: unknown } | null;
        return { at: x.at as number, by: by && Number.isInteger(by.id) ? { id: by.id as number, name: String(by.name ?? '').slice(0, 40) } : null, player: x.player as number, perms: cleanPerms(x.perms) };
      });
    out.factions[fid] = { members, log };
  }
  return out;
}

/** What this player was given in this faction (leaders: everything). */
export function delegatePerms(doc: DelegatesDoc | null, factionId: number, playerId: number, position: string | null | undefined): DelegatePerm[] {
  if (isLeaderPosition(position)) return [...DELEGATE_PERMS];
  return doc?.factions[String(factionId)]?.members[String(playerId)] ?? [];
}

/**
 * THE check, for the server and the bot alike: may this player — `position` from Torn's member list of `factionId`, read
 * with a key (null: not on it) — do `perm` for that faction? Leader and co-leader always; others only when ticked; nobody
 * who isn't on the member list.
 */
export function delegateMay(doc: DelegatesDoc | null, factionId: number, playerId: number, position: string | null | undefined, perm: DelegatePerm): boolean {
  if (position === null || position === undefined) return false;
  return delegatePerms(doc, factionId, playerId, position).includes(perm);
}
